import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VEHICLE_FAQ_CATEGORIES } from "@/content/vehicle-corpus/faq-defs";

export const Route = createFileRoute("/en/vehicle/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/vehicle-corpus");
    return { faqs: m.VEHICLE_FAQ_EN };
  },
  component: EnVehicleFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Vehicles, Driving Licences and Transport in Thailand — ${faqs.length} FAQs`,
        description:
          "Registering and transferring a vehicle as a foreigner, residence certificates, converting a foreign licence, international driving permits, importing and exporting vehicles, road tax and compulsory insurance, traffic fines and land transport law.",
        path: "/en/vehicle/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/vehicle/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Vehicle & Driving FAQ", url: `${site}/en/vehicle/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/vehicle/faq")) },
      ],
    };
  },
});

function EnVehicleFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Vehicle &amp; Driving FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Vehicles, Driving Licences and Transport in Thailand: Registration, Transfer, Import, Road Tax and Certified Documents
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers grounded in the Department of Land Transport rules, the Motor Vehicle Act B.E. 2522 and
          Land Transport Act B.E. 2522, Thai Customs, the Road Traffic Act B.E. 2522, the Protection for Motor Vehicle
          Accident Victims Act B.E. 2535, and the 1949 Geneva and 1968 Vienna Conventions on Road Traffic.
        </p>

        <section aria-label="Topics" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {VEHICLE_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/vehicle/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have our team check your registration or licence pack first</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the task, the province where the vehicle is registered and the country that issued your licence or
            title documents — by phone, LINE or email, Monday to Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="vehicle" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}